import React from 'react';
import { X, Phone, MapPin, Calendar, FileText, CreditCard, User, Check, Package, Clock, Mail } from 'lucide-react';
import { format } from 'date-fns';
import { traduzirMetodoPagamento, pagamentosDoMovimento, formatarResumoPagamentoMisto } from '../../shared/logica/pagamentos';

function DetailRow({ label, value, icon: Icon }) {
  if (!value || value === '') return null;
  
  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon size={14} className="text-slate-400" />}
        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{label}</span>
      </div>
      <span className="text-lg font-bold text-slate-800">{value}</span>
    </div>
  );
}

function DetailSection({ title, children }) {
  return (
    <div className="space-y-3">
      <h3 className="text-[11px] font-black text-slate-400 uppercase tracking-widest">{title}</h3>
      <div className="space-y-4">
        {children}
      </div>
    </div>
  );
}

/**
 * Modal para ver detalhes de um movimento
 */
export default function ModalDetalhesMovimento({ movimento, onClose, onMarkAsDelivered, techJobs = [], budgets = [] }) {
  if (!movimento) return null;

  // Construir endereço completo
  const addressParts = [
    movimento.street,
    movimento.doorNumber,
    movimento.locality,
    movimento.zipCode
  ].filter(Boolean);
  const fullAddress = addressParts.length > 0 ? addressParts.join(', ') : null;

  // Resolver vínculo (se existir)
  const relatedType =
    movimento.relatedType ||
    (movimento.relatedTechJobId ? 'TECH' : (movimento.relatedBudgetId ? 'BUDGET' : null));

  const linkedTech = relatedType === 'TECH' && movimento.relatedTechJobId
    ? techJobs.find(tj => tj.id === movimento.relatedTechJobId)
    : null;

  const linkedBudget = relatedType === 'BUDGET' && movimento.relatedBudgetId
    ? budgets.find(b => b.id === movimento.relatedBudgetId)
    : null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-[60] flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-[3rem] w-full max-w-2xl shadow-2xl p-8 md:p-10 my-8">
        <div className="flex justify-between items-start mb-8">
          <div className="flex-1">
            <h2 className="text-3xl font-black text-slate-900 leading-none mb-2">{movimento.client}</h2>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2 text-green-600 font-bold uppercase text-[10px] tracking-widest">
                <Calendar size={12} />
                <span>{format(new Date(movimento.date), 'dd/MM/yyyy')}</span>
              </div>
              <span className="text-green-600 font-bold uppercase text-[10px] tracking-widest">
                {movimento.type}
              </span>
            </div>
          </div>
          <button 
            onClick={onClose} 
            className="p-2 bg-slate-50 rounded-full hover:bg-slate-100 transition-colors flex-shrink-0"
            aria-label="Fechar"
          >
            <X size={20} />
          </button>
        </div>

        <div className="space-y-8 max-h-[calc(100vh-250px)] overflow-y-auto pr-2">
          {/* Informações do Cliente */}
          <DetailSection title="Informações do Cliente">
            <div className="bg-slate-50 rounded-2xl p-5 border border-slate-200 space-y-4">
              <DetailRow label="Nome" value={movimento.client} icon={User} />
              <DetailRow label="Telefone" value={movimento.phone} icon={Phone} />
              {movimento.nif && <DetailRow label="NIF" value={movimento.nif} icon={FileText} />}
              {movimento.email && <DetailRow label="E-mail" value={movimento.email} icon={Mail} />}
              {fullAddress && (
                <DetailRow label="Morada" value={fullAddress} icon={MapPin} />
              )}
            </div>
          </DetailSection>

          {/* Detalhes de Faturação (quando em outro nome) */}
          {(movimento.useDifferentBilling || movimento.billingClient) && (
            <DetailSection title="Faturação em outro nome">
              <div className="bg-emerald-50/80 rounded-2xl p-5 border border-emerald-100 space-y-4">
                <DetailRow label="Nome para faturação" value={movimento.billingClient} icon={User} />
                {movimento.billingStreet && (
                  <DetailRow
                    label="Morada"
                    value={[movimento.billingStreet, movimento.billingDoorNumber, movimento.billingLocality, movimento.billingZipCode].filter(Boolean).join(', ')}
                    icon={MapPin}
                  />
                )}
                {movimento.billingNif && <DetailRow label="NIF" value={movimento.billingNif} icon={FileText} />}
              </div>
            </DetailSection>
          )}

          {/* Produtos / Serviços */}
          <DetailSection title="Produtos / Serviços">
            {movimento.products && movimento.products.length > 0 ? (
              <div className="space-y-3">
                {movimento.products.map((prod, idx) => (
                  <div key={idx} className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                    <div className="font-bold text-slate-900 text-base mb-1">{prod.name}</div>
                    {prod.ref && (
                      <div className="text-sm text-slate-600 font-semibold">Ref: {prod.ref}</div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                <span className="text-lg font-bold text-slate-800">{movimento.product || '—'}</span>
              </div>
            )}
          </DetailSection>

          {/* Informações de Pagamento */}
          <DetailSection title="Pagamento">
            <div className="bg-slate-50 rounded-2xl p-5 border border-slate-200 space-y-4">
              <DetailRow 
                label="Método de Pagamento" 
                value={movimento.isMixedPayment 
                  ? `Misto (${formatarResumoPagamentoMisto(movimento)})` 
                  : traduzirMetodoPagamento(movimento.paymentMethod)} 
                icon={CreditCard}
              />
              {!movimento.isMixedPayment && movimento.paymentMethod === 'CARD' && movimento.paymentTime1 && (
                <DetailRow label="Hora Pagamento (Multibanco)" value={movimento.paymentTime1} icon={Clock} />
              )}
              {movimento.isMixedPayment && pagamentosDoMovimento(movimento).map((p, idx) => (
                p.method === 'CARD' && p.paymentTime ? (
                  <DetailRow
                    key={idx}
                    label={`Hora Pagamento ${idx + 1} (Multibanco)`}
                    value={p.paymentTime}
                    icon={Clock}
                  />
                ) : null
              ))}
              <DetailRow label="Status Faturação" value={movimento.invoiceStatus?.replace('_', ' ') || 'Não Faturado'} icon={FileText} />
              {movimento.invoiceNumber && movimento.invoiceStatus === 'FATURADO' && (
                <DetailRow label="Número Fatura" value={movimento.invoiceNumber} icon={FileText} />
              )}
            </div>
          </DetailSection>

          {/* Vínculo (Assistência Técnica / Orçamento) */}
          {(movimento.relatedTechJobId || movimento.relatedBudgetId) && (
            <DetailSection title="Vínculo">
              <div className="bg-slate-50 rounded-2xl p-5 border border-slate-200 space-y-4">
                {relatedType === 'TECH' && (
                  <>
                    <DetailRow
                      label="Tipo"
                      value="Assistência Técnica"
                      icon={Package}
                    />
                    <DetailRow
                      label="Associado a"
                      value={
                        linkedTech
                          ? `${linkedTech.client || '—'}${linkedTech.locality ? ` • ${linkedTech.locality}` : ''}${linkedTech.status ? ` • ${linkedTech.status}` : ''}`
                          : `ID: ${movimento.relatedTechJobId}`
                      }
                      icon={User}
                    />
                  </>
                )}
                {relatedType === 'BUDGET' && (
                  <>
                    <DetailRow
                      label="Tipo"
                      value="Orçamento"
                      icon={FileText}
                    />
                    <DetailRow
                      label="Associado a"
                      value={
                        linkedBudget
                          ? `${linkedBudget.client || linkedBudget.location || '—'} • ${(linkedBudget.total || 0).toFixed(2)}€${linkedBudget.status ? ` • ${linkedBudget.status}` : ''}`
                          : `ID: ${movimento.relatedBudgetId}`
                      }
                      icon={User}
                    />
                  </>
                )}
              </div>
            </DetailSection>
          )}

          {/* Entrega */}
          {(movimento.deliveryStatus || movimento.hasDelivery) && (
            <DetailSection title="Entrega">
              <div className="rounded-2xl p-5 border bg-slate-50 border-slate-200">
                {/* Toggle Buttons for Delivery Status */}
                {onMarkAsDelivered && (
                  <div className="flex gap-3 mb-4">
                    <button
                      onClick={() => {
                        if (movimento.deliveryStatus !== 'DELIVERED') {
                          onMarkAsDelivered(movimento);
                        }
                      }}
                      className={`flex-1 py-3 px-4 rounded-xl font-black text-sm uppercase transition-all border-2 ${
                        movimento.deliveryStatus === 'DELIVERED'
                          ? 'bg-green-600 text-white border-green-600 shadow-lg'
                          : 'bg-white text-slate-400 border-slate-200 hover:border-green-300 hover:text-green-600'
                      }`}
                    >
                      <div className="flex items-center justify-center gap-2">
                        <Check size={16} />
                        Entregue
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        if (movimento.deliveryStatus === 'DELIVERED') {
                          onMarkAsDelivered(movimento);
                        }
                      }}
                      className={`flex-1 py-3 px-4 rounded-xl font-black text-sm uppercase transition-all border-2 ${
                        movimento.deliveryStatus !== 'DELIVERED'
                          ? 'bg-blue-600 text-white border-blue-600 shadow-lg'
                          : 'bg-white text-slate-400 border-slate-200 hover:border-blue-300 hover:text-blue-600'
                      }`}
                    >
                      <div className="flex items-center justify-center gap-2">
                        <Package size={16} />
                        Não Entregue
                      </div>
                    </button>
                  </div>
                )}
                
                {/* Delivery Timestamp */}
                {movimento.deliveredAt && movimento.deliveryStatus === 'DELIVERED' && (
                  <div className="flex items-center gap-2 text-sm text-slate-600 mb-3">
                    <Clock size={14} />
                    <span className="font-semibold">
                      Entregue em: {format(new Date(movimento.deliveredAt), 'dd/MM/yyyy HH:mm')}
                    </span>
                  </div>
                )}
                
                {/* Delivery Address */}
                {fullAddress && (
                  <DetailRow 
                    label="Local de Entrega" 
                    value={fullAddress} 
                    icon={MapPin}
                  />
                )}
              </div>
            </DetailSection>
          )}

          {/* Notas */}
          {movimento.notes && (
            <DetailSection title="Observações">
              <div className="bg-amber-50 rounded-2xl p-5 border border-amber-100">
                <p className="text-base text-slate-800 font-semibold whitespace-pre-wrap">{movimento.notes}</p>
              </div>
            </DetailSection>
          )}

          {/* Valor Total */}
          <div className="pt-6 border-t-2 border-dashed border-slate-200">
            <div className="flex justify-between items-center">
              <span className="font-black text-slate-400 uppercase text-sm">Valor Total</span>
              <span className="text-4xl font-black text-slate-900">{movimento.value?.toFixed(2) || '0.00'}€</span>
            </div>
            {movimento.isMixedPayment && pagamentosDoMovimento(movimento).length > 0 && (
              <div className="mt-3 pt-3 border-t border-slate-200 text-right space-y-1">
                {pagamentosDoMovimento(movimento).map((p, idx) => (
                  <div key={idx} className="text-sm text-slate-600 font-semibold">
                    {traduzirMetodoPagamento(p.method)}: {p.value.toFixed(2)}€
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}

